import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  UserPlus,
  Plus,
  Search,
  Phone,
  Mail,
  Building2,
  Euro,
  MoreHorizontal,
  ArrowRight,
  CheckCircle2,
  XCircle,
  Loader2,
  UserCheck,
  Send,
  FileText,
  Trash2
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import api from '../../lib/axios';

// =============================================
// PROSPECTS V1 - Version Simplifiée
// =============================================
// Tableau compact avec actions rapides :
// - Voir détails
// - Envoyer proposition
// - Convertir en client
// - Marquer perdu
// =============================================

const ProspectsPageV1 = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [prospects, setProspects] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  
  // Modals
  const [showNewModal, setShowNewModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [showProposalModal, setShowProposalModal] = useState(false);
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [processing, setProcessing] = useState(false);

  // New prospect form
  const [newProspect, setNewProspect] = useState({
    name: '',
    contact_name: '',
    email: '',
    phone: '',
    service: '',
    estimated_value: '',
    source: 'site_web',
    affiliate_id: '',
    notes: ''
  });

  // Conversion form
  const [conversionData, setConversionData] = useState({
    create_account: true,
    send_welcome_email: true,
    create_project: true,
    project_name: ''
  });

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    new: 0,
    in_progress: 0,
    converted: 0,
    pipeline_value: 0
  });

  const statusConfig = {
    new: { label: 'Nouveau', color: 'bg-blue-100 text-blue-700', icon: UserPlus },
    contacted: { label: 'Contacté', color: 'bg-amber-100 text-amber-700', icon: Phone },
    proposal_sent: { label: 'Proposition envoyée', color: 'bg-purple-100 text-purple-700', icon: Send },
    negotiation: { label: 'Négociation', color: 'bg-orange-100 text-orange-700', icon: FileText },
    won: { label: 'Converti', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
    lost: { label: 'Perdu', color: 'bg-red-100 text-red-700', icon: XCircle },
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [prospectsRes, affiliatesRes] = await Promise.all([
        api.get('/prospects/'),
        api.get('/users/', { params: { role: 'affiliate' } })
      ]);
      
      setProspects(prospectsRes.data || []);
      setAffiliates(affiliatesRes.data || []);
      
      // Calculate stats
      const data = prospectsRes.data || [];
      setStats({
        total: data.length,
        new: data.filter(p => p.status === 'new').length,
        in_progress: data.filter(p => ['contacted', 'proposal_sent', 'negotiation'].includes(p.status)).length,
        converted: data.filter(p => p.status === 'won').length,
        pipeline_value: data.reduce((sum, p) => sum + (parseFloat(p.estimated_value) || 0), 0)
      });
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProspect = async () => {
    if (!newProspect.name || !newProspect.email) {
      toast.error('Nom et email requis');
      return;
    }

    setProcessing(true);
    try {
      await api.post('/prospects/', {
        ...newProspect,
        estimated_value: parseFloat(newProspect.estimated_value) || 0,
        status: 'new'
      });
      toast.success('Prospect créé !');
      setShowNewModal(false);
      setNewProspect({
        name: '', contact_name: '', email: '', phone: '',
        service: '', estimated_value: '', source: 'site_web', affiliate_id: '', notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la création');
    } finally {
      setProcessing(false);
    }
  };

  const handleUpdateStatus = async (prospectId, newStatus) => {
    try {
      await api.put(`/prospects/${prospectId}`, { status: newStatus });
      toast.success('Statut mis à jour');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleConvertToClient = async () => {
    if (!selectedProspect) return;
    
    setProcessing(true);
    try {
      const response = await api.post(`/prospects/${selectedProspect.id}/convert`, {
        ...conversionData,
        project_name: conversionData.project_name || `Projet ${selectedProspect.name}`
      });
      
      toast.success('Prospect converti en client !', {
        description: conversionData.send_welcome_email ? 'Email de bienvenue envoyé' : undefined
      });
      
      setShowConvertModal(false);
      setSelectedProspect(null);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la conversion');
    } finally {
      setProcessing(false);
    }
  };

  const handleSendProposal = async () => {
    if (!selectedProspect) return;
    
    setProcessing(true);
    try {
      await api.post(`/prospects/${selectedProspect.id}/send-proposal`);
      toast.success('Proposition envoyée !');
      setShowProposalModal(false);
      setSelectedProspect(null);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = async (prospectId) => {
    if (!confirm('Supprimer ce prospect ?')) return;
    
    try {
      await api.delete(`/prospects/${prospectId}`);
      toast.success('Prospect supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const filteredProspects = prospects.filter(p => {
    const matchesSearch = 
      p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.contact_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || p.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn(
        "transition-all duration-300 p-6 pt-24",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}>
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#1a365d]">Prospects</h1>
            <p className="text-[#64748b] text-sm mt-1">
              {stats.total} prospects · {stats.pipeline_value.toLocaleString('fr-FR')}€ en pipeline
            </p>
          </div>
          <Button onClick={() => setShowNewModal(true)} className="btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            Nouveau prospect
          </Button>
        </div>

        {/* Stats Cards - Compact */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Total', value: stats.total, color: 'text-[#1a365d]' },
            { label: 'Nouveaux', value: stats.new, color: 'text-blue-600' },
            { label: 'En cours', value: stats.in_progress, color: 'text-amber-600' },
            { label: 'Convertis', value: stats.converted, color: 'text-emerald-600' },
          ].map((stat, idx) => (
            <Card key={idx} className="card">
              <CardContent className="p-4">
                <p className="text-xs text-[#64748b]">{stat.label}</p>
                <p className={cn("text-2xl font-bold", stat.color)}>{stat.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748b]" />
            <Input
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              {Object.entries(statusConfig).map(([key, config]) => (
                <SelectItem key={key} value={key}>{config.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <Card className="card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Prospect</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Service</TableHead>
                <TableHead className="text-right">Valeur</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Source</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#64748b]" />
                  </TableCell>
                </TableRow>
              ) : filteredProspects.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-[#64748b]">
                    Aucun prospect trouvé
                  </TableCell>
                </TableRow>
              ) : (
                filteredProspects.map(prospect => {
                  const status = statusConfig[prospect.status] || statusConfig.new;
                  const StatusIcon = status.icon;
                  return (
                    <TableRow key={prospect.id} className="group">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                            <Building2 className="w-4 h-4 text-slate-500" />
                          </div>
                          <div>
                            <p className="font-medium text-[#1a365d]">{prospect.name}</p>
                            <p className="text-xs text-[#64748b]">{prospect.contact_name}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <p className="text-sm flex items-center gap-1">
                            <Mail className="w-3 h-3" /> {prospect.email}
                          </p>
                          {prospect.phone && (
                            <p className="text-xs text-[#64748b] flex items-center gap-1">
                              <Phone className="w-3 h-3" /> {prospect.phone}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{prospect.service || '-'}</TableCell>
                      <TableCell className="text-right font-mono">
                        {prospect.estimated_value 
                          ? `${parseFloat(prospect.estimated_value).toLocaleString('fr-FR')}€`
                          : '-'
                        }
                      </TableCell>
                      <TableCell>
                        <Badge className={cn("text-xs", status.color)}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {status.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-[#64748b]">
                        {prospect.source === 'affiliate' && prospect.affiliate_name 
                          ? `Apporteur: ${prospect.affiliate_name}`
                          : prospect.source === 'site_web' ? 'Site web' : prospect.source
                        }
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {prospect.status !== 'won' && prospect.status !== 'lost' && (
                              <>
                                <DropdownMenuItem 
                                  onClick={() => handleUpdateStatus(prospect.id, 'contacted')}
                                >
                                  <Phone className="w-4 h-4 mr-2" /> Marquer contacté
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => {
                                    setSelectedProspect(prospect);
                                    setShowProposalModal(true);
                                  }}
                                >
                                  <Send className="w-4 h-4 mr-2" /> Envoyer proposition
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  onClick={() => {
                                    setSelectedProspect(prospect);
                                    setConversionData({
                                      ...conversionData,
                                      project_name: `Projet ${prospect.name}`
                                    });
                                    setShowConvertModal(true);
                                  }}
                                  className="text-emerald-600"
                                >
                                  <UserCheck className="w-4 h-4 mr-2" /> Convertir en client
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => handleUpdateStatus(prospect.id, 'lost')}
                                  className="text-red-600"
                                >
                                  <XCircle className="w-4 h-4 mr-2" /> Marquer perdu
                                </DropdownMenuItem>
                              </>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => handleDelete(prospect.id)}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Card>

        {/* New Prospect Modal */}
        <Dialog open={showNewModal} onOpenChange={setShowNewModal}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Nouveau prospect</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Entreprise *</Label>
                  <Input
                    value={newProspect.name}
                    onChange={(e) => setNewProspect({...newProspect, name: e.target.value})}
                    placeholder="Nom de l'entreprise"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Contact</Label>
                  <Input
                    value={newProspect.contact_name}
                    onChange={(e) => setNewProspect({...newProspect, contact_name: e.target.value})}
                    placeholder="Nom du contact"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={newProspect.email}
                    onChange={(e) => setNewProspect({...newProspect, email: e.target.value})}
                    placeholder="email@exemple.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Téléphone</Label>
                  <Input
                    value={newProspect.phone}
                    onChange={(e) => setNewProspect({...newProspect, phone: e.target.value})}
                    placeholder="06 00 00 00 00"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Service demandé</Label>
                  <Input
                    value={newProspect.service}
                    onChange={(e) => setNewProspect({...newProspect, service: e.target.value})}
                    placeholder="Site web, Logo..."
                  />
                </div>
                <div className="space-y-2">
                  <Label>Budget estimé (€)</Label>
                  <Input
                    type="number"
                    value={newProspect.estimated_value}
                    onChange={(e) => setNewProspect({...newProspect, estimated_value: e.target.value})}
                    placeholder="2000"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Source</Label>
                  <Select 
                    value={newProspect.source} 
                    onValueChange={(v) => setNewProspect({...newProspect, source: v, affiliate_id: v !== 'affiliate' ? '' : newProspect.affiliate_id})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="site_web">Site web</SelectItem>
                      <SelectItem value="recommendation">Recommandation</SelectItem>
                      <SelectItem value="linkedin">LinkedIn</SelectItem>
                      <SelectItem value="affiliate">Apporteur d'affaires</SelectItem>
                      <SelectItem value="other">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {newProspect.source === 'affiliate' && affiliates.length > 0 && (
                  <div className="space-y-2">
                    <Label>Apporteur</Label>
                    <Select 
                      value={newProspect.affiliate_id} 
                      onValueChange={(v) => setNewProspect({...newProspect, affiliate_id: v})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent>
                        {affiliates.map(a => (
                          <SelectItem key={a.id} value={a.id}>
                            {a.company_name || `${a.first_name} ${a.last_name}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={newProspect.notes}
                  onChange={(e) => setNewProspect({...newProspect, notes: e.target.value})}
                  placeholder="Notes sur le prospect..."
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNewModal(false)}>Annuler</Button>
              <Button onClick={handleCreateProspect} disabled={processing} className="btn-primary">
                {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Créer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Convert to Client Modal */}
        <Dialog open={showConvertModal} onOpenChange={setShowConvertModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-emerald-500" />
                Convertir en client
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                <p className="font-medium text-emerald-800">{selectedProspect?.name}</p>
                <p className="text-sm text-emerald-600">{selectedProspect?.email}</p>
              </div>
              
              <div className="space-y-3">
                <label className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:bg-slate-50">
                  <input
                    type="checkbox"
                    checked={conversionData.create_account}
                    onChange={(e) => setConversionData({...conversionData, create_account: e.target.checked})}
                    className="w-4 h-4 rounded"
                  />
                  <div>
                    <p className="font-medium text-[#1a365d]">Créer un compte client</p>
                    <p className="text-xs text-[#64748b]">Le client pourra accéder à son espace</p>
                  </div>
                </label>
                
                <label className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:bg-slate-50">
                  <input
                    type="checkbox"
                    checked={conversionData.send_welcome_email}
                    onChange={(e) => setConversionData({...conversionData, send_welcome_email: e.target.checked})}
                    className="w-4 h-4 rounded"
                  />
                  <div>
                    <p className="font-medium text-[#1a365d]">Envoyer email de bienvenue</p>
                    <p className="text-xs text-[#64748b]">Avec les identifiants de connexion</p>
                  </div>
                </label>
                
                <label className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:bg-slate-50">
                  <input
                    type="checkbox"
                    checked={conversionData.create_project}
                    onChange={(e) => setConversionData({...conversionData, create_project: e.target.checked})}
                    className="w-4 h-4 rounded"
                  />
                  <div>
                    <p className="font-medium text-[#1a365d]">Créer le premier projet</p>
                    <p className="text-xs text-[#64748b]">Basé sur le service demandé</p>
                  </div>
                </label>
              </div>

              {conversionData.create_project && (
                <div className="space-y-2">
                  <Label>Nom du projet</Label>
                  <Input
                    value={conversionData.project_name}
                    onChange={(e) => setConversionData({...conversionData, project_name: e.target.value})}
                    placeholder="Projet ..."
                  />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowConvertModal(false)}>Annuler</Button>
              <Button onClick={handleConvertToClient} disabled={processing} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowRight className="w-4 h-4 mr-2" />}
                Convertir
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Send Proposal Modal */}
        <Dialog open={showProposalModal} onOpenChange={setShowProposalModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Send className="w-5 h-5 text-purple-500" />
                Envoyer une proposition
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200 mb-4">
                <p className="font-medium text-purple-800">{selectedProspect?.name}</p>
                <p className="text-sm text-purple-600">{selectedProspect?.email}</p>
              </div>
              <p className="text-[#64748b] text-sm">
                Une proposition commerciale sera envoyée par email au prospect 
                avec un lien pour accepter et créer son compte.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowProposalModal(false)}>Annuler</Button>
              <Button onClick={handleSendProposal} disabled={processing} className="bg-purple-600 hover:bg-purple-700 text-white">
                {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                Envoyer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default ProspectsPageV1;
